/**
 * /reports/salary-register — every employee's pay for one LOCKED month
 * (persona round 2026-10). Data: GET /api/v1/hr-reports/salary-register.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ReceiptText } from "lucide-react";
import { hrReportsApi, toApiError } from "@/lib/api";
import type { SalaryRegisterRow } from "@/lib/api/hr-reports";
import { ReportsShell } from "@/components/reports/reports-shell";
import { ReportDownloadButton } from "@/components/reports/report-download-button";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPaise } from "@/lib/expense-format";
import { STICKY_FIRST_COL } from "@/lib/pay-table";
import { cn } from "@/lib/utils";
import { StatutoryAmount } from "@/components/payroll/statutory-amount";
import { formatDate } from "@/lib/format";
import { previousPayrollMonth, todayIndiaISO } from "@/lib/payroll-month";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/reports/salary-register")({
  component: SalaryRegisterPage,
});

// PF, ESI and professional tax are one "Statutory" column (hover for the
// split; the CSV download keeps them separate) so the register fits.
const MONEY_BEFORE: readonly { key: keyof SalaryRegisterRow; label: string }[] = [
  { key: "gross_paise", label: "Gross Pay" },
];
const MONEY: readonly { key: keyof SalaryRegisterRow; label: string }[] = [
  { key: "tds_paise", label: "TDS" },
  { key: "total_deductions_paise", label: "Total Deductions" },
  { key: "net_paise", label: "Net Pay" },
  { key: "employer_pf_paise", label: "Employer PF" },
  { key: "employer_esi_paise", label: "Employer ESI" },
  { key: "cost_to_company_paise", label: "Cost to Company" },
];

function SalaryRegisterPage() {
  const last = previousPayrollMonth(todayIndiaISO());
  const [month, setMonth] = useState(`${last.year}-${String(last.month).padStart(2, "0")}`);
  const valid = /^\d{4}-\d{2}$/.test(month);
  const q = useQuery({
    queryKey: ["hr-reports", "salary-register", month],
    queryFn: ({ signal }) => hrReportsApi.getSalaryRegister(month, signal),
    enabled: valid,
  });
  const rep = q.data;

  return (
    <ReportsShell
      title="Salary Register"
      description="Each employee's pay for a locked month — earnings, deductions, net pay and the employer's cost."
      actions={
        <ReportDownloadButton
          report="salary-register"
          params={{ month }}
          filename={`salary-register-${month}.csv`}
          disabled={!rep?.locked}
        />
      }
    >
      <div className="space-y-6">
        <div>
          <Label htmlFor="sr-month">Month</Label>
          <Input
            id="sr-month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-48"
          />
        </div>
        {q.isLoading && <Skeleton className="h-64" />}
        {q.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn&apos;t load the register</AlertTitle>
            <AlertDescription>{toApiError(q.error).message}</AlertDescription>
          </Alert>
        )}
        {rep && !rep.locked && (
          <EmptyState
            icon={ReceiptText}
            title={`Payroll for ${rep.label} isn't locked yet`}
            description="The salary register is available once the month's payroll is locked."
          />
        )}
        {rep && rep.locked && (
          <Card>
            <CardContent className="overflow-x-auto px-0 pt-4 sm:px-6">
              {rep.locked_on && (
                <p className="mb-3 px-6 text-sm text-muted-foreground sm:px-0">
                  {rep.label} · locked on {formatDate(rep.locked_on)} · {rep.rows.length} employees
                </p>
              )}
              <Table data-testid="salary-register-table">
                <TableHeader>
                  <TableRow className="max-sm:[&>th:not(:first-child)]:whitespace-nowrap">
                    <TableHead className={cn(STICKY_FIRST_COL, "min-w-[8.5rem] max-w-[11rem] sm:min-w-[11rem] sm:max-w-none")}>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Paid Days</TableHead>
                    <TableHead className="text-right">LOP Days</TableHead>
                    {MONEY_BEFORE.map((c) => (
                      <TableHead key={c.key} className="text-right">
                        {c.label}
                      </TableHead>
                    ))}
                    <TableHead className="text-right" title="PF + ESI + professional tax — hover a figure for the split">
                      Statutory
                    </TableHead>
                    {MONEY.map((c) => (
                      <TableHead key={c.key} className="text-right">
                        {c.label}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rep.rows.map((r, i) => (
                    <TableRow key={`${r.employee_code}-${r.name}-${i}`}>
                      <TableCell className={cn(STICKY_FIRST_COL, "min-w-[8.5rem] max-w-[11rem] sm:min-w-[11rem] sm:max-w-none")}>
                        <span className="font-medium">{r.name}</span>
                        {r.employee_code && (
                          <span className="block font-mono text-xs text-muted-foreground">{r.employee_code}</span>
                        )}
                      </TableCell>
                      <TableCell>{r.department}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.paid_days || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.lop_days || 0}</TableCell>
                      {MONEY_BEFORE.map((c) => (
                        <TableCell key={c.key} className="text-right font-mono tabular-nums">
                          {formatPaise(r[c.key] as number)}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-mono tabular-nums">
                        <StatutoryAmount pf={r.pf_paise} esi={r.esi_paise} pt={r.pt_paise} />
                      </TableCell>
                      {MONEY.map((c) => (
                        <TableCell key={c.key} className="text-right font-mono tabular-nums">
                          {formatPaise(r[c.key] as number)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 font-semibold">
                    <TableCell className={cn(STICKY_FIRST_COL, "min-w-[8.5rem] max-w-[11rem] sm:min-w-[11rem] sm:max-w-none")}>Total</TableCell>
                    <TableCell colSpan={3} />
                    {MONEY_BEFORE.map((c) => (
                      <TableCell key={c.key} className="text-right font-mono tabular-nums">
                        {formatPaise(rep.total[c.key] as number)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-mono tabular-nums">
                      <StatutoryAmount pf={rep.total.pf_paise} esi={rep.total.esi_paise} pt={rep.total.pt_paise} />
                    </TableCell>
                    {MONEY.map((c) => (
                      <TableCell key={c.key} className="text-right font-mono tabular-nums">
                        {formatPaise(rep.total[c.key] as number)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </ReportsShell>
  );
}
